/**
 * Cost-Bisect — Git-Commit Cost Snapshots (Patent Sub-system I)
 * ==============================================================
 * The `git bisect` of cost regressions. Walk commit history, find the exact
 * commit where your bill jumped.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  GitCommit, GitBranch, Search, AlertTriangle, TrendingUp, TrendingDown,
  Loader2, FlaskConical, CheckCircle2, Crosshair,
} from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import ProvenByTests from '../components/ProvenByTests';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => {
  if (n == null) return '$0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(2)}`;
};

const fmtDate = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const CostBisect = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bisecting, setBisecting] = useState(false);
  const [result, setResult] = useState(null);
  const [demoQuery, setDemoQuery] = useState(null);

  const [fromSha, setFromSha] = useState('');
  const [toSha, setToSha] = useState('');
  const [threshold, setThreshold] = useState('1500');

  useEffect(() => {
    axios.get(`${API_URL}/cost-bisect/demo?limit=50`)
      .then((r) => {
        setHistory(r.data.history);
        const dq = r.data.demo_query;
        setDemoQuery(dq);
        setFromSha(dq.from_sha);
        setToSha(dq.to_sha);
        setThreshold(String(dq.threshold_usd));
      })
      .catch(() => toast.error('Could not load demo history'))
      .finally(() => setLoading(false));
  }, []);

  const runBisect = async () => {
    setBisecting(true);
    setResult(null);
    try {
      const r = await axios.post(`${API_URL}/cost-bisect/bisect-demo`, {
        from_sha: fromSha.trim(),
        to_sha: toSha.trim(),
        threshold_usd: parseFloat(threshold) || 1000,
      });
      setResult(r.data);
      if (r.data.found) {
        toast.success(`Regression found at ${r.data.regression_commit.commit_sha.slice(0, 7)}`);
      } else {
        toast.info('No commit in range exceeded threshold');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bisect failed');
    } finally {
      setBisecting(false);
    }
  };

  // Quick stats for the timeline
  const maxCost = Math.max(...history.map((s) => s.projected_monthly_usd || 0));
  const minCost = Math.min(...history.map((s) => s.projected_monthly_usd || 0));
  const costRange = Math.max(1, maxCost - minCost);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="cost-bisect-page">
      <Helmet>
        <title>Cost-Bisect — Git-Commit Cost Snapshots | CARTIEAI</title>
        <meta name="description" content="The git bisect of cost regressions. Walk commit history, find the exact PR where your bill jumped. Patent-pending sub-system I." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-cyan-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-5" data-testid="bisect-patent-badge">
            <FlaskConical className="w-3 h-3" />
            Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
            Cost-Bisect
          </h1>
          <div className="mt-3"><ProvenByTests engine="bisect" /></div>
          <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            The <span className="text-white font-semibold">git bisect</span> of cost regressions. Walk commit history to find the exact PR where your bill jumped.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Every commit on <code className="text-cyan-300 bg-slate-900 px-1 rounded">main</code> carries a cost-state snapshot. When your bill spikes, <code className="text-cyan-300 bg-slate-900 px-1 rounded">cartie cost-bisect HEAD~50 HEAD</code> walks the range and surfaces the offending commit in seconds.
          </p>
        </div>

        {/* BISECT TOOL */}
        <Card className="mt-10 bg-slate-900/60 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Crosshair className="w-5 h-5 text-cyan-300" /> Run a bisect
            </CardTitle>
            <CardDescription>Find the first commit in the range whose cost-delta exceeded the threshold.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label htmlFor="from-sha" className="text-xs text-slate-400">From commit</Label>
                <Input id="from-sha" value={fromSha} onChange={(e) => setFromSha(e.target.value)} className="bg-slate-950 border-white/10 text-white font-mono text-xs mt-1" data-testid="bisect-from-input" />
              </div>
              <div>
                <Label htmlFor="to-sha" className="text-xs text-slate-400">To commit</Label>
                <Input id="to-sha" value={toSha} onChange={(e) => setToSha(e.target.value)} className="bg-slate-950 border-white/10 text-white font-mono text-xs mt-1" data-testid="bisect-to-input" />
              </div>
              <div>
                <Label htmlFor="threshold" className="text-xs text-slate-400">Threshold ($)</Label>
                <Input id="threshold" type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="bg-slate-950 border-white/10 text-white font-mono text-xs mt-1" data-testid="bisect-threshold-input" />
              </div>
              <div className="flex items-end">
                <Button onClick={runBisect} disabled={bisecting} className="w-full bg-emerald-500 hover:bg-cyan-400 text-slate-950 font-semibold" data-testid="bisect-run-btn">
                  {bisecting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Search className="w-4 h-4 mr-1.5" />}
                  Bisect
                </Button>
              </div>
            </div>

            {/* RESULT */}
            {result && result.found && result.regression_commit && (
              <div className="mt-6 rounded-md border border-red-500/40 bg-red-500/10 p-4" data-testid="bisect-result-found">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-red-200 font-semibold text-sm">Regression located</div>
                    <div className="text-xs text-slate-300 mt-1.5 font-mono">{result.regression_commit.commit_sha.slice(0, 12)}</div>
                    <div className="text-white text-sm mt-2 font-medium">&quot;{result.regression_commit.commit_subject}&quot;</div>
                    <div className="text-xs text-slate-400 mt-1">by <span className="text-cyan-300">{result.regression_commit.author}</span> · {fmtDate(result.regression_commit.ts)}</div>
                    <div className="text-xs text-amber-300 mt-2 flex items-center gap-2">
                      <TrendingUp className="w-3.5 h-3.5" />
                      Cost jumped <span className="font-mono font-semibold">{fmtUsd(result.delta_usd)}</span> in this commit
                    </div>
                    <div className="text-xs text-slate-400 mt-3 leading-relaxed">{result.explanation}</div>
                  </div>
                </div>
              </div>
            )}
            {result && !result.found && (
              <div className="mt-6 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200" data-testid="bisect-result-clean">
                <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
                {result.explanation}
              </div>
            )}
          </CardContent>
        </Card>

        {/* COMMIT TIMELINE */}
        <Card className="mt-8 bg-slate-900/60 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <GitCommit className="w-5 h-5 text-cyan-300" /> Commit cost timeline
            </CardTitle>
            <CardDescription>50 commits from the demo seed · regression deliberately planted at commit #38 for testing.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 text-center text-slate-500 text-sm"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading history…</div>
            ) : (
              <div className="space-y-1" data-testid="bisect-timeline">
                {history.map((s, idx) => {
                  const isRegression = result?.regression_commit?.commit_sha === s.commit_sha;
                  const barPct = ((s.projected_monthly_usd - minCost) / costRange) * 100;
                  const isDeltaPositive = s.delta_vs_parent_usd > 0;
                  return (
                    <div
                      key={s.commit_sha}
                      className={`group rounded px-3 py-2 ${isRegression ? 'bg-red-500/10 border border-red-500/40' : 'hover:bg-slate-950/60'} transition`}
                      data-testid={`bisect-commit-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <code className={`text-[11px] font-mono ${isRegression ? 'text-red-300' : 'text-slate-500'} w-20 flex-shrink-0`}>
                          {s.commit_sha.slice(0, 9)}
                        </code>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs truncate ${isRegression ? 'text-white font-semibold' : 'text-slate-300'}`}>
                              {s.commit_subject}
                            </span>
                            {isRegression && <Badge className="bg-red-500 text-white text-[9px] flex-shrink-0">REGRESSION</Badge>}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">{s.author} · {fmtDate(s.ts)}</div>
                        </div>
                        <div className="text-right flex-shrink-0 w-28">
                          <div className="text-xs font-mono text-white">{fmtUsd(s.projected_monthly_usd)}</div>
                          <div className={`text-[10px] font-mono ${isDeltaPositive ? 'text-amber-400' : s.delta_vs_parent_usd < 0 ? 'text-emerald-400' : 'text-slate-300'}`}>
                            {s.delta_vs_parent_usd > 0 ? '+' : ''}{fmtUsd(s.delta_vs_parent_usd)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-1.5 h-1 bg-slate-950 rounded overflow-hidden">
                        <div
                          className={`h-full transition-all ${isRegression ? 'bg-red-400' : isDeltaPositive ? 'bg-amber-400/60' : 'bg-cyan-400/60'}`}
                          style={{ width: `${Math.max(2, barPct)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* HOW IT WORKS */}
        <div className="mt-16 grid md:grid-cols-3 gap-4">
          <Card className="bg-slate-900/60 border-white/10">
            <CardContent className="p-5">
              <GitBranch className="w-5 h-5 text-cyan-300 mb-2" />
              <div className="text-white font-semibold text-sm">1 · Every commit gets a snapshot</div>
              <div className="text-xs text-slate-400 mt-2 leading-relaxed">A 30-line GitHub Action computes the projected monthly cost-state from your IaC + traffic profile, on every push to main.</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-white/10">
            <CardContent className="p-5">
              <GitCommit className="w-5 h-5 text-cyan-300 mb-2" />
              <div className="text-white font-semibold text-sm">2 · We index by SHA</div>
              <div className="text-xs text-slate-400 mt-2 leading-relaxed">Snapshots are persisted per-tenant, queryable by commit SHA range. No source code leaves your repo — only the cost-state lands here.</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-white/10">
            <CardContent className="p-5">
              <Search className="w-5 h-5 text-cyan-300 mb-2" />
              <div className="text-white font-semibold text-sm">3 · Bisect when bill spikes</div>
              <div className="text-xs text-slate-400 mt-2 leading-relaxed"><code className="bg-slate-950 px-1 rounded text-cyan-300">cartie cost-bisect HEAD~50 HEAD --threshold 1500</code> returns the exact commit that broke margin.</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CostBisect;
